import React, { useState, useEffect } from 'react';
import {
    Box,
    Typography,
    Paper,
    Tabs,
    Tab,
    List,
    ListItem,
    ListItemText,
    ListItemSecondaryAction,
    IconButton,
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Chip,
    Fade,
    CircularProgress,
    Alert
} from '@mui/material';
import {
    RestoreFromTrash,
    DeleteForever,
    Business,
    Dashboard as DashboardIcon,
    ArrowBack
} from '@mui/icons-material';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { useThemeMode } from '../contexts/ThemeContext';
import CatLoadingSpinner from '../components/CatLoadingSpinner';
import CatEmptyState from '../components/CatEmptyState';

interface DeletedBusiness {
    id: string;
    name: string;
    description: string;
    deletedAt: string;
}

interface DeletedBoard {
    id: string;
    name: string;
    type: string;
    businessId: string;
    business: {
        name: string;
    };
    deletedAt: string;
}

const TrashPage: React.FC = () => {
    const [tabValue, setTabValue] = useState(0);
    const [deletedBusinesses, setDeletedBusinesses] = useState<DeletedBusiness[]>([]);
    const [deletedBoards, setDeletedBoards] = useState<DeletedBoard[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
    const [itemToDelete, setItemToDelete] = useState<{ id: string, type: 'business' | 'board', businessId?: string } | null>(null);
    const navigate = useNavigate();
    const { mode: themeMode } = useThemeMode();

    const API_URL = process.env.REACT_APP_API_URL || 'https://api.turn.ar/';

    useEffect(() => {
        fetchTrashData();
    }, []);

    const fetchTrashData = async () => {
        setIsLoading(true);
        try {
            const [businessesRes, boardsRes] = await Promise.all([
                axios.get(`${API_URL}/api/businesses/trash`),
                axios.get(`${API_URL}/api/boards/trash`)
            ]);
            setDeletedBusinesses(businessesRes.data);
            setDeletedBoards(boardsRes.data);
        } catch (error) {
            console.error('Error fetching trash data:', error);
        } finally {
            setIsLoading(false);
        }
    };

    const handleRestoreBusiness = async (id: string) => {
        try {
            await axios.put(`${API_URL}/api/businesses/${id}/restore`);
            fetchTrashData();
        } catch (error) {
            console.error('Error restoring business:', error);
        }
    };

    const handleRestoreBoard = async (id: string) => {
        try {
            await axios.put(`${API_URL}/api/boards/${id}/restore`);
            fetchTrashData();
        } catch (error) {
            console.error('Error restoring board:', error);
        }
    };

    const handlePermanentDelete = async () => {
        if (!itemToDelete) return;

        try {
            if (itemToDelete.type === 'business') {
                await axios.delete(`${API_URL}/api/businesses/${itemToDelete.id}/permanent`);
            } else {
                await axios.delete(`${API_URL}/api/businesses/${itemToDelete.businessId}/boards/${itemToDelete.id}/permanent`);
            }
            setConfirmDeleteOpen(false);
            setItemToDelete(null);
            fetchTrashData();
        } catch (error) {
            console.error('Error permanently deleting item:', error);
        }
    };

    const openDeleteConfirm = (id: string, type: 'business' | 'board', businessId?: string) => {
        setItemToDelete({ id, type, businessId });
        setConfirmDeleteOpen(true);
    };

    if (isLoading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
                <CatLoadingSpinner message="Buscando en la basura..." />
            </Box>
        );
    }

    return (
        <Box sx={{ p: 4, maxWidth: 1000, mx: 'auto' }} className="cat-paw-background">
            <Button
                startIcon={<ArrowBack />}
                onClick={() => navigate('/dashboard')}
                sx={{ mb: 3, color: 'text.secondary' }}
            >
                Volver al Dashboard
            </Button>

            <Paper elevation={0} sx={{ p: 4, borderRadius: 4, background: themeMode === 'dark' ? 'rgba(30,30,30,0.9)' : 'rgba(255,255,255,0.9)', backdropFilter: 'blur(10px)' }}>
                <Typography variant="h4" sx={{ mb: 3, fontWeight: 'bold', color: 'text.primary' }} className="cat-gradient-text">
                    🗑️ Papelera de Reciclaje
                </Typography>

                <Alert severity="info" sx={{ mb: 4, borderRadius: 2 }}>
                    Los elementos en la papelera se eliminarán permanentemente después de 30 días.
                </Alert>

                <Tabs
                    value={tabValue}
                    onChange={(e, v) => setTabValue(v)}
                    sx={{ mb: 3, borderBottom: 1, borderColor: 'divider' }}
                    textColor="primary"
                    indicatorColor="primary"
                >
                    <Tab label={`Negocios (${deletedBusinesses.length})`} icon={<Business />} iconPosition="start" />
                    <Tab label={`Tableros (${deletedBoards.length})`} icon={<DashboardIcon />} iconPosition="start" />
                </Tabs>

                {tabValue === 0 && (
                    <Fade in>
                        <Box>
                            {deletedBusinesses.length === 0 ? (
                                <CatEmptyState message="No hay negocios en la papelera" />
                            ) : (
                                <List>
                                    {deletedBusinesses.map((business) => (
                                        <Paper key={business.id} sx={{ mb: 2, borderRadius: 2, overflow: 'hidden', border: themeMode === 'dark' ? '1px solid rgba(80,80,80,0.3)' : '1px solid rgba(0,0,0,0.05)' }}>
                                            <ListItem>
                                                <ListItemText
                                                    primary={
                                                        <Typography variant="h6" fontWeight="bold">
                                                            {business.name}
                                                        </Typography>
                                                    }
                                                    secondary={
                                                        <>
                                                            <Typography variant="body2" color="text.secondary">
                                                                {business.description}
                                                            </Typography>
                                                            <Typography variant="caption" color="error">
                                                                Eliminado el: {new Date(business.deletedAt).toLocaleDateString()}
                                                            </Typography>
                                                        </>
                                                    }
                                                />
                                                <ListItemSecondaryAction>
                                                    <Button
                                                        startIcon={<RestoreFromTrash />}
                                                        onClick={() => handleRestoreBusiness(business.id)}
                                                        sx={{ mr: 1 }}
                                                        color="primary"
                                                    >
                                                        Restaurar
                                                    </Button>
                                                    <IconButton
                                                        edge="end"
                                                        color="error"
                                                        onClick={() => openDeleteConfirm(business.id, 'business')}
                                                    >
                                                        <DeleteForever />
                                                    </IconButton>
                                                </ListItemSecondaryAction>
                                            </ListItem>
                                        </Paper>
                                    ))}
                                </List>
                            )}
                        </Box>
                    </Fade>
                )}

                {tabValue === 1 && (
                    <Fade in>
                        <Box>
                            {deletedBoards.length === 0 ? (
                                <CatEmptyState message="No hay tableros en la papelera" />
                            ) : (
                                <List>
                                    {deletedBoards.map((board) => (
                                        <Paper key={board.id} sx={{ mb: 2, borderRadius: 2, overflow: 'hidden', border: themeMode === 'dark' ? '1px solid rgba(80,80,80,0.3)' : '1px solid rgba(0,0,0,0.05)' }}>
                                            <ListItem>
                                                <ListItemText
                                                    primary={
                                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                            <Typography variant="h6" fontWeight="bold">
                                                                {board.name}
                                                            </Typography>
                                                            <Chip label={board.business.name} size="small" variant="outlined" />
                                                        </Box>
                                                    }
                                                    secondary={
                                                        <>
                                                            <Typography variant="body2" color="text.secondary">
                                                                Tipo: {board.type}
                                                            </Typography>
                                                            <Typography variant="caption" color="error">
                                                                Eliminado el: {new Date(board.deletedAt).toLocaleDateString()}
                                                            </Typography>
                                                        </>
                                                    }
                                                />
                                                <ListItemSecondaryAction>
                                                    <Button
                                                        startIcon={<RestoreFromTrash />}
                                                        onClick={() => handleRestoreBoard(board.id)}
                                                        sx={{ mr: 1 }}
                                                        color="primary"
                                                    >
                                                        Restaurar
                                                    </Button>
                                                    <IconButton
                                                        edge="end"
                                                        color="error"
                                                        onClick={() => openDeleteConfirm(board.id, 'board', board.businessId)}
                                                    >
                                                        <DeleteForever />
                                                    </IconButton>
                                                </ListItemSecondaryAction>
                                            </ListItem>
                                        </Paper>
                                    ))}
                                </List>
                            )}
                        </Box>
                    </Fade>
                )}
            </Paper>

            {/* Confirm Delete Dialog */}
            <Dialog
                open={confirmDeleteOpen}
                onClose={() => setConfirmDeleteOpen(false)}
            >
                <DialogTitle sx={{ fontWeight: 'bold', color: 'error.main' }}>
                    ¿Eliminar permanentemente?
                </DialogTitle>
                <DialogContent>
                    <Typography>
                        Esta acción no se puede deshacer. El elemento desaparecerá para siempre.
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setConfirmDeleteOpen(false)}>Cancelar</Button>
                    <Button onClick={handlePermanentDelete} color="error" variant="contained" startIcon={<DeleteForever />}>
                        Eliminar para siempre
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default TrashPage;

